// @react
import React, { useState, useContext, ReactElement } from 'react'
// @types
import { ProfileFormValues } from 'modules/common/forms/types/profile'
import { GetUserQuery, GetUserQueryResult } from 'lib/graphql/graphqlTypes'
// @libraries
import { DevTool } from '@hookform/devtools'
import strings from 'lib/constants/strings'
// @graphQL
// @context
import { PersonalInformationContext } from 'modules/profile/context'
// @components
import Grid from '@bit/atd.web.grid'
import icons from '@bit/atd.web.icons/dist/getIcons'
import Banner from '@bit/atd.web.banner'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import EditAction from 'modules/profile/components/EditAction'
import EditingAction from 'modules/profile/components/EditingAction'
import AboutFields from 'modules/common/forms/fields/profile/About'
import useBioFormWrapper from 'modules/common/forms/hooks/useBioFormWrapper'
import { ApolloQueryResult } from '@apollo/client'

type PropsType = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
  refetch: () => Promise<ApolloQueryResult<GetUserQuery>>
}

const BioForm = ({
  data: userData,
  isLoading,
  refetch,
}: PropsType): ReactElement => {
  /****************************************
   * PROPS
   *****************************************/

  const isProfileVisible = userData?.user?.visibility.publicProfile
  const privacyNoticeText: {
    icon: keyof typeof icons
    title: string
    body: string
  } = isProfileVisible
    ? {
        icon: 'lock-off',
        title: strings.PROFILE.BIO.PROFILE_IS_ON,
        body: strings.PROFILE.BIO.VISIBILITY_AVAILABLE_WARNING,
      }
    : {
        icon: 'lock-on',
        title: strings.PROFILE.BIO.PROFILE_IS_OFF,
        body: strings.PROFILE.BIO.VISIBILITY_AVAILABLE_WARNING_OFF,
      }

  /****************************************
   * STATE
   *****************************************/

  const [editing, setEditing] = useState<boolean>(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)

  /****************************************
   * HOOKS
   *****************************************/
  const {
    formMethods,
    control,
    setImageFile,
    userProfileUpdateHandler,
  } = useBioFormWrapper({
    data: userData,
    isLoading,
    refetch,
  })

  const profileInformation = useContext(PersonalInformationContext)
  const { errors } = formMethods.formState

  /****************************************
   * GQL HOOKS
   *****************************************/

  /****************************************
   * FUNCTIONS
   *****************************************/

  const onEditHandler = () => {
    profileInformation.setEditingSection('bio')
    setEditing(true)
  }

  const onCancelHandler = () => {
    profileInformation.setEditingSection(null)
    setEditing(false)
    formMethods.clearErrors()
    formMethods.reset()
  }

  const onSubmitHandler = async (data: ProfileFormValues) => {
    setIsSubmitting(true)
    const res = await userProfileUpdateHandler(data)
    setIsSubmitting(false)
    if (res) {
      setEditing(false)
      profileInformation.setEditingSection(null)
    }
  }

  /****************************************
   * RENDER
   *****************************************/

  const form = (
    <>
      {process.env.DEBUG === '1' && <DevTool control={control} />}

      <form>
        <Grid style={{ position: 'relative', zIndex: 0 }} container spacing={4}>
          {/* Banner */}
          <Grid xs={12} item>
            <Banner
              fullWidth
              icon={privacyNoticeText.icon}
              header={privacyNoticeText.title}
            >
              {privacyNoticeText.body}:
            </Banner>
          </Grid>

          <AboutFields
            editing={editing}
            control={control}
            formMethods={formMethods}
            errors={errors}
            setImageFile={setImageFile}
            userData={userData}
          />
        </Grid>
      </form>
    </>
  )

  const box = (content: ReactElement): ReactElement => {
    return (
      <Box
        header={{
          title: !editing
            ? strings.PROFILE.BIO.BIO_SHORT
            : strings.PROFILE.BIO.BIO_EDITING,
          actions: !editing ? (
            <EditAction disabled={isLoading} onClick={() => onEditHandler()} />
          ) : (
            <EditingAction
              bottom
              isSubmitting={isMutationSubmitting}
              onCancel={() => onCancelHandler()}
              onSubmit={formMethods.handleSubmit((data: any) =>
                onSubmitHandler(data),
              )}
            />
          ),
        }}
      >
        {content}
      </Box>
    )
  }

  return !isLoading ? box(form) : box(<SkeletonLoader />)
}

export default BioForm
